/**
 * Scene store — T-1.2.1 (data plane) + T-1.3.5 (undo/redo stacks).
 *
 * Source of truth: SPEC_Technical.md §2.1 (数据结构) + ADR-001 + SPEC §4.3
 * (undo stack depth ≥50).
 *
 * This is the **single source of truth** for the Math3D scene at runtime.
 * It owns:
 *   - `voxels`         : 体素表 (Map<VoxelCoord, VoxelProps>) — Constitution §2.2
 *   - `selection`      : 选中体素 id 集合 (Set<string>) — FR-303
 *   - `ui`             : 工具态/模式态/视图态 (FR-304..307)
 *   - `anchorGroup`    : FR-304 锚定高亮组
 *   - `undoStack`      : Command[] — T-1.3.5 (SPEC §4.3, depth capped at 50)
 *   - `redoStack`      : Command[] — T-1.3.5
 *
 * **Critical separation** (T-1.1.5 hand-off note):
 *   - This store is the *data* plane. The render layer (MainScene) and
 *     UI plane (Toolbar) are *commands*. M3's mode-2 anchor linkage is
 *     the first place where the two planes communicate, and that link
 *     goes through commands (T-1.3.x), not through holding a MainScene
 *     ref here. **Do not add a `mainScene` slot to this store.**
 *
 * Immer v10 supports Map/Set natively — assignment-style updates
 * (`state.voxels = next`) inside `set(...)` are translated to
 * immutable produces. The four setters below intentionally replace the
 * entire Map/Set; per-key/per-id diffing lives in the Command layer
 * (M2 onwards), not here.
 *
 * Why `setUI` is a merge not a replace:
 *   - UI is a single logical record; replacing it would force every
 *     caller to re-supply every field. A partial merge keeps callers
 *     (Toolbar / command side-effects / tests) cheap.
 *
 * T-1.3.5 — undo/redo stacks:
 *   - These slots are owned here (the store is the single source of
 *     truth), but the SEMANTICS (push / pop / cap / branch-invalidate)
 *     live in `src/commands/Command.ts`. The Command helpers take a
 *     `CommandHostState` — any object that exposes `voxels` /
 *     `undoStack` / `redoStack` — so they can be unit-tested on a
 *     plain Map-shaped stub without booting the store.
 *   - The Command helpers read the live store via
 *     `useSceneStore.getState()` and write back via `setUndoStack` /
 *     `setRedoStack`, so subscribers still observe a coherent
 *     transition (one set() call → one notification).
 */

import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';
import type { Command } from '../commands/Command';
import type { FaceName, VoxelCoord, VoxelProps } from './types';

// Immer v10 made Map/Set drafting opt-in (smaller bundle by default).
// SPEC §2.1 mandates `voxels: Map` and `selection/visibleLayers/anchorGroup: Set`,
// so we MUST enable the plugin before any `set(...)` drafts Map/Set fields.
// Idempotent — safe to call multiple times.
enableMapSet();

/** FR-305..307 — UI mode/filter state. Mirrors SPEC §2.1 verbatim. */
export interface UIState {
  tool: 'place' | 'move' | 'select' | 'paint' | 'label' | 'unfold';
  paintColor: string;
  /** FR-306 — empty = all layers visible. */
  visibleLayers: Set<number>;
  layerStackMode: 'all' | 'single' | 'stack-up';
  /** FR-307 — view filter, NOT a Command (see SPEC §4.2). */
  cutPlane: { axis: 'x' | 'y' | 'z'; pos: number; mode: 'remove' | 'transparent' } | null;
  viewMode: 'normal' | 'transparent-hidden' | 'only-visible';
  /** FR-304 — mode-2 (locked-direction view) toggle. */
  mode2Active: boolean;
  mode2Direction: FaceName | null;
}

/** Top-level scene state — SPEC §2.1 + SPEC §4.3 (T-1.3.5). */
export interface SceneState {
  voxels: Map<VoxelCoord, VoxelProps>;
  selection: Set<string>;
  ui: UIState;
  /** FR-304 — anchor highlight group; null when no anchor is active. */
  anchorGroup: Set<string> | null;
  /**
   * Undo stack — T-1.3.5. SPEC §4.3 mandates depth ≥50; the cap lives in
   * `UNDO_STACK_LIMIT` (Command.ts) so the Command helpers and the
   * store agree on the bound without a circular import.
   *
   * Commands are NOT part of the immer draft in practice — the
   * Command helpers mutate the stacks directly after the produce()
   * wrapper has finished. The store only OWNS the slots; the Command
   * layer owns the semantics (push/undo/redo).
   */
  undoStack: Command[];
  redoStack: Command[];
}

/** Setter actions. Keep this surface minimal until M2 commands land. */
export interface SceneActions {
  setVoxels(next: Map<VoxelCoord, VoxelProps>): void;
  setSelection(next: Set<string>): void;
  /** Partial merge — call sites stay terse (T-1.1.5 + future commands). */
  setUI(next: Partial<UIState>): void;
  setAnchorGroup(next: Set<string> | null): void;
  /**
   * Replace the undo stack — T-1.3.5. Exposed so command callers can
   * `setUndoStack([...useSceneStore.getState().undoStack, cmd])` after
   * applying a command, and so future code paths (load/save, M6
   * collapse-undo) can rewrite the stack as a unit. Keeping it as a
   * single `setXxx` rather than `pushUndo`/`popUndo` keeps the
   * Command layer responsible for stack semantics.
   */
  setUndoStack(next: Command[]): void;
  /** Replace the redo stack — T-1.3.5. Same rationale as setUndoStack. */
  setRedoStack(next: Command[]): void;
}

/** Default UIState — matches SPEC §2.1. */
export const DEFAULT_UI_STATE: UIState = {
  tool: 'place',
  paintColor: '#4A90E2',
  visibleLayers: new Set<number>(),
  layerStackMode: 'all',
  cutPlane: null,
  viewMode: 'normal',
  mode2Active: false,
  mode2Direction: null,
};

/** Default SceneState — useful for tests + future reset command. */
export const DEFAULT_SCENE_STATE: SceneState = {
  voxels: new Map<VoxelCoord, VoxelProps>(),
  selection: new Set<string>(),
  ui: DEFAULT_UI_STATE,
  anchorGroup: null,
  undoStack: [],
  redoStack: [],
};

export const useSceneStore = create<SceneState & SceneActions>()(
  immer((set) => ({
    voxels: DEFAULT_SCENE_STATE.voxels,
    selection: DEFAULT_SCENE_STATE.selection,
    ui: DEFAULT_UI_STATE,
    anchorGroup: DEFAULT_SCENE_STATE.anchorGroup,
    undoStack: [],
    redoStack: [],

    // The cast inside each setter widens the readonly-typed Map/Set
    // contract from SPEC §2.1 to the mutable draft slot immer v10
    // exposes inside `set(...)`. The structural difference is purely
    // the `readonly` modifier on the tuple key — at runtime the Map is
    // an opaque reference and assignment is a swap, not a per-key
    // mutation. The `// eslint-disable-next-line @typescript-eslint/no-explicit-any`
    // is justified: the alternative is to drop `readonly` from SPEC §2.1
    // (a public contract change) or to write per-key/Set diffing here
    // (a Command-layer concern, deferred to M2).
    setVoxels: (next) =>
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.voxels = next as any;
      }),
    setSelection: (next) =>
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.selection = next as any;
      }),
    setUI: (next) =>
      set((state) => {
        state.ui = { ...state.ui, ...next };
      }),
    setAnchorGroup: (next) =>
      set((state) => {
        state.anchorGroup = next;
      }),
    // T-1.3.5 — undo/redo stacks are plain Command[] arrays. The
    // Command helpers (pushUndo / undo / redo) operate on the arrays
    // *outside* the immer producer; they read the store snapshot via
    // `getState()` and write back through `setUndoStack` /
    // `setRedoStack` so subscribers still see a coherent transition.
    // The casts mirror the Map/Set pattern above — Command is a
    // structural type (just `{ type, apply, revert }`), and assignment
    // to the immer draft slot is the same op at runtime.
    setUndoStack: (next) =>
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.undoStack = next as any;
      }),
    setRedoStack: (next) =>
      set((state) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        state.redoStack = next as any;
      }),
  })),
);
